import React, { memo, useEffect, useState } from 'react'
import CommonDialog from '../../../../../common/components/dialog/CommonDialog'
import styles from './teammember.module.scss'
import { SolarProduct } from '../../../../../model/solar-product/solar-product'
import { useDispatch } from 'react-redux'
import { showLoader, hideLoader } from '../../../../../stores/slices/loader-slice'
import { toast } from 'react-toastify'
import { usePauseProductMutation } from '../../../../../service/solar-product-service'
import { getErrorMessage } from '../../../../../utils/error-handler'
import { UtilityTeamMember } from '../../../../../model/utility-users/utility-team-member'
import { useDeleteTeamMemberMutation, useEditTeamMemberMutation, useInviteTeamMemberMutation } from '../../../../../service/utility-users-service'
import CommonSideDialog from '../../../../../common/components/side-dialog/CommonSideDialog'
import { useForm } from 'react-hook-form'
import CommonFormFieldWrapper, { getFormInputErrors } from '../../../../../common/components/form-field-wrapper/CommonFormFieldWrapper'
import Select from '../../../../../common/components/new/select/Select'
import { useLazyGetRolesQuery } from '../../../../../service/role-service'
import { useInviteNewTeamMemberMutation } from '../../../../../service/solar-provider-service'
import Button from '../../../../../common/components/xepp/button/Button'

type Props = {
    user: UtilityTeamMember | undefined
    isEdit: boolean
    onCloseDialog: () => void
    onDone: () => void
}
type RoleDto = {
    id: string;
    name: string;
}
type EditTeamMemberState = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    role: RoleDto
}

const TeamMemberSideDialog: React.FC<Props> = memo((props: Props) => {

    const [getRoles, { data: roles ,isSuccess: isRoleSuccess}] = useLazyGetRolesQuery();
    const [inviteTeamMember,{isSuccess: isInviteSuccess, isError: isInviteError, error: inviteError}] = useInviteTeamMemberMutation();
    const [editTeamMember,{isSuccess: isEditSuccess, isError: isEditError , error: editError}] = useEditTeamMemberMutation();
    const [allRoles, setAllRoles] = useState<RoleDto[]>([]);

    var defaultValues: EditTeamMemberState = {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        role: {
            id: '',
            name: ''
        }
    }
    if (props.user != undefined) {
        defaultValues = {
            firstName: props.user.firstName,
            lastName: props.user.lastName,
            email: props.user.email,
            phoneNumber: props.user.phoneNumber,
            role: {
                id: props.user.roles[0].id,
                name: props.user.roles[0].name
            }
        }
    }

    const {
        register,
        handleSubmit,
        watch,
        control,
        setValue,
        reset,
        formState: { errors, isValid, isSubmitted, touchedFields },
    } = useForm<EditTeamMemberState>({
        defaultValues: defaultValues,
        reValidateMode: "onChange",
        mode: "onTouched",
    });

    const submitForm = (data: EditTeamMemberState) => {
        try{
            const trimmed = {
                email: data.email.trim(),
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                phoneNumber: data.phoneNumber,
                roleId: data.role.id
            };
            if (props.isEdit) {
                editTeamMember(trimmed)
            } else {
                inviteTeamMember(trimmed)
            }            
        }
        catch(error: any){
            toast.error(getErrorMessage(error.data) ?? 'An Error occurred');
        }
    }

    useEffect(() => {
        getRoles({ Page: 1, PageSize: 50 });

    }, [getRoles])

    useEffect(() => {
        if (roles !== undefined) {
            setAllRoles(roles!.items.map((x) => { return { id: x.id, name: x.name } }));
        }
    }, [roles])

    useEffect(()=>{
        if(isEditSuccess || isInviteSuccess){
            
            toast.success(`User ${defaultValues.email} successfully ${props.isEdit ? "edited":"invited"}`)
            props.onDone();
        }
    },[isEditSuccess,isInviteSuccess])

    useEffect(()=>{
        if(isEditError && editError){
            toast.error(getErrorMessage((editError as any).data) ?? 'An Error occurred');
        }
    },[isEditError,editError])

    useEffect(()=>{
        if(isInviteError && inviteError){
            toast.error(getErrorMessage((inviteError as any).data) ?? 'An Error occurred');
        }
    },[isInviteError,inviteError])
    
    return (
        <CommonSideDialog onCloseDialog={props.onCloseDialog}>
            <div className={styles.SideDialogContent}>

                <div className={styles.TitleContent}>
                    <span className={styles.Title}> {props.isEdit ? 'Edit' : 'Add New'} Team Member</span>
                    <span className={styles.Subtitle}> {props.isEdit ? '' : 'Invite new members by email address'}</span>
                </div>
                <div className={styles.TeamMemberForm}>
                    <div className={styles.OneRow}>
                        <CommonFormFieldWrapper title="First Name" required={true} errors={getFormInputErrors(errors.firstName?.message)} >
                            <input
                                {...register("firstName", {
                                    required: "First Name is required",
                                })}
                                placeholder="First Name"
                            />
                        </CommonFormFieldWrapper>
                        <CommonFormFieldWrapper title="Last Name" required={true} errors={getFormInputErrors(errors.lastName?.message)}>
                            <input
                                {...register("lastName", {
                                    required: "Last name is required",
                                })}
                                placeholder="Last Name"
                            />
                        </CommonFormFieldWrapper>
                    </div>

                    <CommonFormFieldWrapper title="Email" required={true} errors={getFormInputErrors(errors.email?.message)}>
                        <input
                            {...register("email", {
                                required: "Email is required",
                            })}
                            disabled = {props.isEdit}
                            placeholder="testemail@gmail.com"
                        />
                    </CommonFormFieldWrapper>
                    <CommonFormFieldWrapper title="Phone Number" required={true} errors={getFormInputErrors(errors.phoneNumber?.message)}>
                        <input
                            {...register("phoneNumber", {
                                required: "Phone Number is required",
                            })}
                            placeholder="+234811042736"
                        />
                    </CommonFormFieldWrapper>
                    {allRoles &&
                    
                        <Select
                            {...register("role", {
                                required: "Role is required",
                            })}
                            label="Role"
                            placeholder="Select role"
                            required
                            error={errors.role?.message}
                            searchable
                            selected={{ key: watch('role')?.id, label: watch('role')?.name }}
                            items={allRoles.map((x) => { return { key: x.id, label: x.name }; })}
                            onChange={value => setValue('role', { id: value?.key, name: value?.label ?? "" })} />
                    
                    }
                </div>
                <div className={styles.ActionButtons}>
                    <Button text={"Cancel"} variant={'outline'} onClick={props.onCloseDialog} />
                    <Button text={"Save"} onClick={handleSubmit(submitForm)} />
                </div>
            </div>

        </CommonSideDialog>
    )
})

export default TeamMemberSideDialog